import { createStore, ObservableMap } from "@stencil/store";
import { registerGlobal, getRegisteredGlobal } from "@trumpf/ux.utils";
import { debounce } from "../../../utils/debounce";
import {
    ContentApiError,
    ContentData,
    ContentDataProvider,
    ContentDataProviderCallback,
    ContentDataProviderState,
} from "./interface";
import { requestContentData } from "../api";
import { NS_CONTENT_DATA_PROVIDER } from "../constants";
import { FilterDataProvider, getFilterDataProvider } from "../filter-provider";

/**
 * ContentDataProvider Implemenmtation
 */
class ContentDataProviderInstance<T> implements ContentDataProvider<T> {
    private dataPromise: Promise<ContentData<T> | null> | null = null;
    private readonly scope: string;
    private readonly store: ObservableMap<ContentDataProviderState<T>>;
    private readonly subscribers: ContentDataProviderCallback<T>[] = [];
    private nextPage: string | null = null;

    constructor(scope: string) {
        this.scope = scope;

        // Initialize local store
        this.store = createStore<ContentDataProviderState<T>>({
            data: null,
            endpoint: null,
            error: null,
            pending: false,
            query: "",
            sort: "",
        });

        // Write session data to store
        const sessionData = this.readSessionStorage();
        if (sessionData.query) {
            this.store.set("query", sessionData.query);
        }
        if (sessionData.sort) {
            this.store.set("sort", sessionData.sort);
        }

        const filterProvider: FilterDataProvider = getFilterDataProvider(
            this.scope
        );
        filterProvider.subscribe(({ filterQuery }) => {
            this.setQuery(filterQuery);
        });

        // Fetch data if endpoint or query have changed
        this.store.onChange("endpoint", this.fetchDataDebounced);
        this.store.onChange("query", () => {
            this.nextPage = null; // when the query is changed, we want to load the first page again
            this.fetchDataDebounced();
        });

        // Notify subscribers if state has changed
        this.store.onChange("data", this.notifySubscribersDebounced);
        this.store.onChange("error", this.notifySubscribersDebounced);
        this.store.onChange("pending", this.notifySubscribersDebounced);
    }

    get endpoint(): string {
        return this.state.endpoint;
    }

    get query(): string {
        return this.state.query;
    }

    get sort(): string {
        return this.state.sort;
    }

    get data(): ContentData<T> | null {
        return this.state.data;
    }

    get error(): ContentApiError | null {
        return this.state.error;
    }

    get pending(): boolean {
        return this.state.pending;
    }

    get state() {
        return this.store.state;
    }

    private getNamespace = () => `${NS_CONTENT_DATA_PROVIDER}.${this.scope}`;

    private readSessionStorage = () => {
        const jsonStr = window.sessionStorage.getItem(this.getNamespace());
        return typeof jsonStr === "string" ? JSON.parse(jsonStr) : {};
    };

    private updateSessionStorage = () => {
        const data = {
            ...this.readSessionStorage(),
            query: this.query,
            sort: this.sort,
        };
        window.sessionStorage.setItem(
            this.getNamespace(),
            JSON.stringify(data)
        );
    };

    getData(): Promise<ContentData<T> | null> {
        if (this.dataPromise === null) {
            this.dataPromise = this.fetchData();
        }
        return this.dataPromise;
    }

    getQueryParam(key: string): string | null {
        const params = new URLSearchParams(this.query);
        return params.get(key) || null;
    }

    setEndpoint(value: string) {
        this.store.set("endpoint", value || null);
    }

    setQuery(value: string) {
        const params = new URLSearchParams(value);
        if (this.sort !== "") {
            params.set("sort", this.sort);
        }
        // Omit empty query parameters
        params.forEach((value, key, parent) => {
            if (value === "" && !key.endsWith("[]")) {
                parent.delete(key);
            }
        });
        this.store.set("query", params.toString());
        this.updateSessionStorage();
    }

    setQueryParam(key: string, value: string) {
        const params = new URLSearchParams(this.query);
        params.set(key, value);
        this.setQuery(params.toString());
    }

    setSortParam(value: string) {
        this.store.set("sort", value);
        this.setQueryParam("sort", value);
    }

    loadMore(): void {
        if (this.nextPage !== null) {
            this.getData();
        }
    }

    loadFirstPage(): void {
        this.nextPage = null;
        this.fetchDataDebounced();
    }

    subscribe(handler: ContentDataProviderCallback<T>): () => void {
        if (!this.subscribers.includes(handler)) {
            this.subscribers.push(handler);
        }
        return () => this.unsubscribe(handler);
    }

    unsubscribe(handler: ContentDataProviderCallback<T>): void {
        const index = this.subscribers.indexOf(handler);
        if (index !== -1) {
            this.subscribers.splice(index, 1);
        }
    }

    private fetchData = async (): Promise<ContentData<T> | null> => {
        if (this.endpoint === null) {
            return Promise.resolve(null);
        }
        const url = this.nextPage !== null ? this.nextPage : this.endpoint;
        this.store.set("pending", true);
        const response = await requestContentData<T>(url, this.query);
        let data: ContentData<T> | null = null;
        if (response.ok) {
            data = await response.json();
            if (this.endpoint === url) {
                this.store.set("data", data);
            } else {
                this.store.set("data", {
                    ...this.data,
                    ...(data.filteredCount !== undefined && {
                        filteredCount: data.filteredCount,
                    }),
                    ...(data.displayedCount !== undefined && {
                        displayedCount: data.displayedCount,
                    }),
                    items: [...this.data.items, ...data.items],
                });
            }
            this.store.set("error", data.error || null);
        } else {
            this.store.set("data", null);
            this.store.set("error", {
                status: response.status,
                message: response.statusText,
            });
        }
        if (data && typeof data.nextPage === "string" && data.nextPage !== "") {
            this.nextPage = data.nextPage;
        } else {
            this.nextPage = null;
        }
        this.store.set("pending", false);
        this.dataPromise = null;
        return data;
    };

    private fetchDataDebounced = debounce(this.fetchData, 20);

    private notifySubscribers = () => {
        this.subscribers.forEach((handler) => {
            handler(this.state);
        });
    };

    private notifySubscribersDebounced = debounce(this.notifySubscribers, 20);
}

/**
 * Get the ContentDataProvider singleton for a specific scope
 * @param scope
 * @returns {ContentDataProvider}
 */
export function getContentDataProvider<T = any>(
    scope: string
): ContentDataProvider<T> {
    if (!/^[a-zA-Z0-9-_]+$/.test(scope)) {
        throw new Error("invalid scope");
    }
    const ns: string = `${NS_CONTENT_DATA_PROVIDER}.${scope}`;
    let provider = getRegisteredGlobal<ContentDataProvider<T>>(ns) || null;
    if (provider === null) {
        provider = new ContentDataProviderInstance<T>(scope);
        registerGlobal(provider, ns);
    }
    return provider;
}
